import { Button, TextArea, Typography } from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import { SavedAddress as Address } from "api/resources/payment/types"
import Card from "components/Card"
import CollapseWrapper from "components/CollapseWrapper"
import GoogleLocationSelect, {
  AddressComponents,
  AddressTypeEnum,
} from "components/GoogleLocationSelect.tsx"
import { context } from "global/Context/context"
import { useContext, useEffect, useState } from "react"
import { Controller, useForm, useWatch } from "react-hook-form"
import { mapErrorsV2 } from "utils/helpers"
import useCurrentCountry from "utils/hooks/useCurrentCountry"

export type NewAddress = {
  address: string | null
  country: string | null
  state: string | null
  location: AddressComponents | null
}

type Props = {
  onAdd: (address: Address) => void
  onChangeClicked: () => void
}

const AddAddress = (props: Props) => {
  const [showForm, setShowForm] = useState(true)

  const { profile } = useContext(context)

  const currentCountry = useCurrentCountry()

  const {
    register,
    control,
    setError,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<NewAddress>()

  const createAddress = useMutation({
    mutationFn: async (data: NewAddress) => {
      const country = data.location?.find(item =>
        item.types.includes(AddressTypeEnum.COUNTRY)
      )
      const state = data.location?.find(item =>
        item.types.includes(AddressTypeEnum.STATE)
      )

      setValue("state", state?.longText || "")
      setValue("country", country?.longText || "")

      const res = await api.payment.createAddress({
        data: {
          address: data.address ?? null,
          pincode: null,
          city: null,
          country: country
            ? { name: country.longText, isoCode: country.shortText }
            : null,
          state: state
            ? { name: state.longText, isoCode: state.shortText }
            : null,
        },
      })

      updateDataInProfile(res)

      return res
    },
    onSuccess: res => {
      props.onAdd(res)
      setShowForm(false)
    },
    onError: err => {
      mapErrorsV2(err, {
        setter: setError,
        fields: [
          ["address", "location"],
          ["pincode", "location"],
          ["city", "location"],
          ["country", "location"],
          ["state", "location"],
        ],
      })
    },
  })

  const updateDataInProfile = (res: Address) => {
    try {
      const profileUpdateData: {
        country_name?: string
        state_name?: string
      } = {}
      if (!profile.country && res.country) {
        profileUpdateData.country_name = res.country.name
      }
      if (!profile.state && res.state) {
        profileUpdateData.state_name = res.state.name
      }

      // Trigger update profile API call to update data in the profile
      // This is just to update the profile data. It serves no other purpose
      // Also no need to await this
      api.profile.update({
        data: profileUpdateData,
      })
    } catch (error) {
      console.error("Error updating profile data:", error)
    }
  }

  const onSubmit = handleSubmit(data => {
    createAddress.mutate(data)
  })

  const locationValue = useWatch({
    control,
    name: "location",
  })

  useEffect(() => {
    const handleAddressCreation = async () => {
      if (locationValue && !createAddress.isPending) {
        const addressData: NewAddress = {
          location: locationValue,
          address: null,
          country: null,
          state: null,
        }
        await createAddress.mutate(addressData)
      }
    }

    handleAddressCreation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationValue])

  useEffect(() => {
    if (currentCountry)
      setValue("location", [
        {
          longText: currentCountry.name,
          shortText: currentCountry.isoCode,
          types: [AddressTypeEnum.COUNTRY],
        },
      ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCountry])

  return (
    <Card padding={2} className="mt-2 rounded-lg">
      <div>
        <CollapseWrapper open={!showForm} className="overflow-visible">
          <div className="flex w-full items-center justify-between">
            <Typography variant="strong" className="text-end">
              {[getValues("state"), getValues("country")]
                .filter(Boolean)
                .join(", ")}
            </Typography>
            <Button
              variant="text"
              onClick={() => {
                props.onChangeClicked()
                setShowForm(true)
              }}
            >
              Change{" "}
              <span className="hidden sm:inline-block">&nbsp;Address</span>
            </Button>
          </div>
        </CollapseWrapper>
        <CollapseWrapper open={showForm} className="overflow-visible">
          <form onSubmit={onSubmit}>
            <Controller
              control={control}
              name="location"
              render={({ field: { onChange, value } }) => (
                <GoogleLocationSelect
                  fullWidth
                  label="Country/Region"
                  required
                  className="w-full"
                  placeHolder="Enter your location"
                  errors={errors.location?.message}
                  helperText={
                    "Required to calculate appropriate government taxes"
                  }
                  onChange={val => {
                    onChange(val)
                  }}
                  value={value}
                />
              )}
              rules={{
                required: { value: true, message: "Required" },
              }}
            />

            <div className="mb-0.75 mt-2">
              <Typography variant="strong">
                Billing Address{" "}
                <Typography
                  variant="body"
                  className="inline text-onSurface-500"
                >
                  (Optional)
                </Typography>
              </Typography>
            </div>
            <TextArea
              rows={3}
              errors={errors.address?.message}
              placeholder="Building number, street name"
              {...register("address")}
            />
            <Button
              color="black"
              type="submit"
              className="mt-3 w-full"
              loading={createAddress.isLoading}
            >
              Save
            </Button>
          </form>
        </CollapseWrapper>
      </div>
    </Card>
  )
}

export default AddAddress
